import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import type { HeavyDutyMachineryOptionIcon } from '../constants/heavyDutyMachineryOptionIcons';
import GearSpinner from './GearSpinner';

interface WizardIllustrationProps {
  icon: HeavyDutyMachineryOptionIcon;
  /** Used only to key the icon's own enter animation so it replays on
   * every phase change, even between two phases that happen to share an
   * icon. */
  phaseKey: string;
}

// Fixed per-render so the dots don't jump to new random spots on every
// parent re-render (typing in a field on the same phase shouldn't restart
// the animation) -- only remounts (a new `key` from the phase index) picks
// fresh positions.
const GEAR_COUNT = 6;

/**
 * Publish wizard's left-panel decorative animation -- a big soft icon badge
 * over a curved "wave" shape, with a handful of small gears drifting slowly
 * upward and fading (see the .wizard-illustration-gear keyframes in
 * index.css). They used to be round white dots -- the last of the spa/water
 * language this app was built out of. Deliberately simple CSS rather than Lottie/an asset pipeline
 * (no such library is wired into this app yet) -- subtle on purpose, per
 * the wizard's own celebration screen being the one animation allowed to be
 * more dramatic.
 */
const WizardIllustration: React.FC<WizardIllustrationProps> = ({ icon: Icon, phaseKey }) => {
  const gears = useMemo(
    () =>
      Array.from({ length: GEAR_COUNT }, (_, i) => ({
        left: 10 + ((i * 37) % 80),
        // 10-24px rather than the 6-19 the dots used: below about 10px a
        // gear's teeth stop resolving and it is just a speck, which is
        // fine for a dot and pointless for a gear.
        size: 10 + ((i * 11) % 15),
        delay: (i * 0.8) % 4,
        duration: 4 + ((i * 3) % 3),
      })),
    []
  );

  return (
    <div style={{ position: 'relative', width: '100%', height: 220, overflow: 'hidden' }}>
      {/* The wave -- a plain circle big enough that only its top arc shows,
          anchored past the bottom edge of its own container. A fixed, subtle
          white-on-dark tint works against any of the panel's own per-phase
          background colours without needing to compute a lighter shade of
          each one. */}
      <div
        style={{
          position: 'absolute',
          bottom: -220,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 640,
          height: 440,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.10)',
        }}
      />
      {/* The drift (translate + fade, see the CSS class) sits on the SVG
          itself, and each gear's own turn lives inside it -- two separate
          transforms that compose rather than fight, so a gear rises and
          turns at the same time. */}
      {gears.map((gear, i) => (
        <GearSpinner
          key={i}
          className="wizard-illustration-gear"
          size={gear.size}
          durationSeconds={6 + (i % 3) * 2}
          reverse={i % 2 === 1}
          style={{
            left: `${gear.left}%`,
            bottom: 30,
            color: 'rgba(255, 255, 255, 0.55)',
            animationDelay: `${gear.delay}s`,
            animationDuration: `${gear.duration}s`,
          }}
        />
      ))}
      <motion.div
        key={phaseKey}
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        style={{
          position: 'absolute',
          top: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 72,
          height: 72,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.16)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={34} color="#fff" />
      </motion.div>
    </div>
  );
};

export default WizardIllustration;
